import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { useBillingPlans, useCreatePaymentUrl, useCreateSubscriptionUrl } from "@/hooks/useBilling"
import { RecurringPlan } from "@/types/api"
import { LoadingOverlay } from "@/components/ui/loading-overlay"
import { useToast } from "@/hooks/useToast"
import Loader from "@/components/ui/loader"
import { useActiveOrganization, useUpdateActiveOrganization } from "@/hooks/useOrganization"
import { useListOrganizations } from "@/hooks/useOrganization"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { isOrganizationPlanCancelled } from "@/lib/utils/organization"

export default function BillingSettings() {
  const [activeTab, setActiveTab] = useState<"recurring" | "pay-as-you-go">("recurring")
  const [searchParams, setSearchParams] = useSearchParams()
  const { toast } = useToast()

  const { data: billingData, isLoading: isBillingLoading, error } = useBillingPlans()
  const createPaymentUrl = useCreatePaymentUrl()
  const createSubscriptionUrl = useCreateSubscriptionUrl()

  const [isLoading, setIsLoading] = useState(false)

  const { refetch: refetchOrganizations } = useListOrganizations()
  const activeOrganization = useActiveOrganization()
  const updateActiveOrganization = useUpdateActiveOrganization()

  // Handle payment success/failure and organization refresh
  useEffect(() => {
    const success = searchParams.get("success")

    if (success === "false") {
      toast({
        title: "Payment Failed",
        description: "There was an issue processing your payment. Please try again.",
        variant: "destructive",
      })
      searchParams.delete("success")
      setSearchParams(searchParams, { replace: true })
    } else if (success === "true") {
      toast({
        title: "Payment Successful",
        description: "Your payment has been processed successfully.",
        variant: "success",
      })
      searchParams.delete("success")
      setSearchParams(searchParams, { replace: true })

      // Only refetch organizations on successful payment
      refetchOrganizations().then(({ data }) => {
        if (data && activeOrganization) {
          const updatedOrganization = data.find((org) => org.id === activeOrganization.id)
          if (updatedOrganization) {
            updateActiveOrganization(updatedOrganization)
          }
        }
      })
    }
  }, [searchParams, toast, setSearchParams, refetchOrganizations, activeOrganization, updateActiveOrganization])

  const getCostPerCredit = (plan: RecurringPlan) => {
    if (plan.custom_pricing) return null

    const price = plan.amount / 100

    const creditsFeature = plan.features.credits_per_month
    if (!creditsFeature?.value) return null

    const credits = parseInt(creditsFeature.value.replace(/,/g, ""), 10)

    if (credits <= 0 || isNaN(credits) || isNaN(price)) return null
    if (price <= 0) return null

    const costPerCredit = price / credits

    if (isNaN(costPerCredit) || !isFinite(costPerCredit)) return null

    return costPerCredit
  }

  const formatAmount = (amount: number) => {
    return (amount / 100).toFixed(0)
  }

  const formatCredits = (credits: number) => {
    if (credits === -1) return "Unlimited"
    return credits.toLocaleString()
  }

  const handleSubscribe = (planId: string) => {
    createPaymentUrl.mutate(planId)
    setIsLoading(true)
  }

  const handleBuyCredits = (planId: string) => {
    if (!hasActiveSubscription) {
      toast({
        title: "Subscription required",
        description: "Activate a recurring subscription before buying credits.",
        variant: "warning",
      })
      return
    }
    createPaymentUrl.mutate(planId)
    setIsLoading(true)
  }

  const handlePlanAction = () => {
    createSubscriptionUrl.mutate()
    setIsLoading(true)
  }

  const hasActiveSubscription =
    !!activeOrganization?.plan &&
    !isOrganizationPlanCancelled(activeOrganization.plan) &&
    activeOrganization.plan.status === "active" &&
    ["starter", "growth", "enterprise"].includes(activeOrganization.plan.plan_type)

  // Get button configuration based on current plan
  const getPlanButtonConfig = (plan: RecurringPlan) => {
    if (!activeOrganization?.plan || isOrganizationPlanCancelled(activeOrganization.plan)) {
      return { text: "Subscribe", variant: "primary" }
    }

    const currentPlanType = activeOrganization.plan.plan_type
    const isCurrentPlan = plan.name.toLowerCase() === currentPlanType

    if (isCurrentPlan) {
      return { text: "Manage", variant: "secondary" }
    }

    if (currentPlanType === "starter" && plan.name === "Growth") {
      return { text: "Upgrade", variant: "primary" }
    }

    if (currentPlanType === "growth" && plan.name === "Starter") {
      return { text: "Downgrade", variant: "secondary" }
    }

    return { text: "Subscribe", variant: "primary" }
  }

  if (isBillingLoading) {
    return (
      <div className="mx-auto max-w-5xl border-none">
        <div className="flex h-64 items-center justify-center">
          <Loader />
        </div>
      </div>
    )
  }

  if (error || !billingData) {
    return (
      <div className="mx-auto max-w-5xl border-none">
        <div className="flex h-64 items-center justify-center">
          <div className="text-red-500 dark:text-red-400">Failed to load billing plans</div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl border-none px-4">
      <div className="mb-10 flex w-full justify-center">
        <div className="inline-flex rounded-lg bg-gray-100 p-1 dark:bg-default">
          <button
            className={`rounded-md px-4 py-1 text-sm font-medium transition-colors ${activeTab === "recurring" ? "bg-white text-black shadow dark:bg-variant dark:text-white" : "text-gray-800 hover:text-black dark:text-gray-300 dark:hover:text-white"}`}
            onClick={() => setActiveTab("recurring")}
          >
            Recurring
          </button>
          <button
            className={`rounded-md px-4 py-1 text-sm font-medium transition-colors ${activeTab === "pay-as-you-go" ? "bg-white text-black shadow dark:bg-variant dark:text-white" : "text-gray-800 hover:text-black dark:text-gray-300 dark:hover:text-white"}`}
            onClick={() => setActiveTab("pay-as-you-go")}
          >
            Pay as you go
          </button>
        </div>
      </div>

      {activeTab === "recurring" ? (
        <div className="flex flex-col gap-3 md:flex-row md:gap-5">
          {billingData.recurring.map((plan) => {
            const costPerCredit = getCostPerCredit(plan)

            return (
              <div
                key={plan.slug}
                className="relative flex flex-1 flex-col rounded-2xl border border-gray-200 bg-white p-8 shadow-sm transition hover:shadow-lg dark:border-gray-800 dark:bg-default"
              >
                {plan.name === "Growth" && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 transform">
                    <span className="rounded-full bg-black px-3 py-1 text-xs font-semibold text-white dark:bg-white dark:text-black">
                      Popular
                    </span>
                  </div>
                )}
                <div className="mb-2 text-lg font-semibold text-black dark:text-white">{plan.name}</div>
                <div className="mb-2 text-gray-700 dark:text-gray-300">{plan.description}</div>
                {!plan.custom_pricing ? (
                  <div className="mb-6 flex items-end gap-2">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      ${formatAmount(plan.amount)}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">USD</span>
                  </div>
                ) : (
                  <div className="mb-6 flex min-h-[48px] items-end gap-2">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">Custom</span>
                  </div>
                )}
                {!plan.custom_pricing ? (
                  <div className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                    per {plan.billing_cycle}
                    {costPerCredit ? ` • $${costPerCredit.toFixed(3)} per credit` : ""}
                  </div>
                ) : (
                  <div className="mb-4 text-xs text-gray-500 dark:text-gray-400"></div>
                )}
                {plan.custom_pricing ? (
                  <button className="mb-6 w-full rounded-md border border-black bg-white px-4 py-2 text-sm font-semibold text-black shadow-sm transition hover:bg-gray-100 dark:border-white dark:bg-[#0F0F12] dark:text-white dark:hover:bg-gray-700">
                    Contact Us
                  </button>
                ) : (
                  (() => {
                    const buttonConfig = getPlanButtonConfig(plan)
                    const isManage = buttonConfig.text === "Manage"
                    const isSecondary = buttonConfig.variant === "secondary"

                    return (
                      <button
                        className={`mb-6 w-full rounded-md px-4 py-2 text-sm font-semibold shadow-sm transition ${isSecondary
                            ? "border border-black bg-white text-black hover:bg-gray-100 dark:border-white dark:bg-variant dark:text-white dark:hover:bg-gray-700"
                            : "bg-black text-white hover:bg-gray-900 dark:bg-white dark:text-black dark:hover:bg-gray-100"
                          }`}
                        onClick={isManage ? handlePlanAction : () => handleSubscribe(plan.slug)}
                        disabled={createPaymentUrl.isPending || createSubscriptionUrl.isPending}
                      >
                        {buttonConfig.text}
                      </button>
                    )
                  })()
                )}
                <ul className="space-y-2 text-sm">
                  {Object.entries(plan.features).map(([key, feature]) => {
                    return (
                      <li
                        key={key}
                        className={`flex items-center ${feature.available ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-gray-500"}`}
                      >
                        <span className={`mr-2 ${feature.available ? "text-black dark:text-white" : ""}`}>+</span>
                        {feature.custom_label
                          ? feature.value
                          : feature.value
                            ? `${feature.label}: ${feature.value}`
                            : feature.label}
                      </li>
                    )
                  })}
                </ul>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="flex justify-center">
          <div className="mb-5 w-full max-w-2xl rounded-2xl border border-gray-200 bg-white p-7 shadow-sm dark:border-gray-700 dark:bg-default">
            <div className="mb-5 text-center">
              <div className="mb-2 text-2xl font-semibold text-black dark:text-white">Pay as you go</div>
              <div className="text-gray-700 dark:text-gray-300">
                Purchase credits as needed for enrichment and chat beyond your plan limits.
              </div>
            </div>

            {!hasActiveSubscription && (
              <Alert variant="warning" className="my-4">
                <AlertTitle>Subscription required</AlertTitle>
                <AlertDescription>You need an active subscription before buying additional credits.</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {billingData.payg.map((option) => {
                return (
                  <button
                    key={option.slug}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-6 py-8 text-center transition hover:bg-gray-100 focus:outline-none dark:border-gray-700 dark:bg-variant dark:hover:bg-variant/20"
                    onClick={() => handleBuyCredits(option.slug)}
                    disabled={createPaymentUrl.isPending}
                  >
                    <div className="mb-2 text-2xl font-bold text-black dark:text-white">
                      ${formatAmount(option.amount)}
                    </div>
                    <div className="mb-2 text-sm text-gray-600 dark:text-gray-300">
                      {formatCredits(option.credits)} credits
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      ~${option.rate_per_credit.toFixed(3)} per credit
                    </div>
                  </button>
                )
              })}
            </div>

            {/* Features Section */}
            <div className="mt-8 border-t border-gray-200 pt-8 dark:border-gray-600">
              <h3 className="mb-4 text-center text-lg font-semibold text-black dark:text-white">Features Included</h3>
              <div className="mx-auto grid max-w-2xl grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="mr-2 text-green-500">✓</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">Unlimited enrichments and chat</span>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2 text-green-500">✓</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">All AI models and features</span>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2 text-green-500">✓</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">Long input processing</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="mr-2 text-green-500">✓</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">Team collaboration</span>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2 text-green-500">✓</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">Shared workflows</span>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2 text-green-500">✓</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">No monthly commitment</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {(createPaymentUrl.isPending || createSubscriptionUrl.isPending || isLoading) && (
        <LoadingOverlay text="Processing payment..." />
      )}
    </div>
  )
}
